import React, { createContext, useState } from 'react'
import { HStack, Box, Stack, Button, Icon, Spacer, Text, Flex, useDisclosure, useToast } from '@chakra-ui/react'
import { Card } from 'components/card/card'
import { TicketsInlineTable } from 'features/ticket-page/tickets-inline-table'
import { ResidentBox } from 'features/ticket-page/resident-box'
import { FinancialOverview } from 'features/ticket-page/financial-overview'
import { BiBookAdd } from 'react-icons/bi'
import { useNavigate, useParams } from 'react-router-dom'
import { useProperty } from 'api/property'
import { NewTicketFlow } from 'features/ticket-page/new-ticket-flow'
import Status from 'features/common/status'

import { useDeletePropertyMutation, usePCProject, usePropertyAllowDelete } from 'api/pc-projects'
import { TicketDetails } from 'features/ticket-page/ticket-details'
import { useUserRolesSelector } from 'utils/redux-common-selectors'
import { ConfirmationBox } from 'components/Confirmation'

type EditContextProps = {
  selectedTicketId: number | null
  setSelectedTicketId: React.Dispatch<React.SetStateAction<number | null>>
}
export const TicketEditContext = createContext<EditContextProps>({} as EditContextProps)

const TicketEdit: React.FC = () => {
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null)
  const [selectedWoId, setSelectedWoId] = useState<number | null>(null)

  const { projectId: propertyId } = useParams<'projectId'>()
  useProperty(propertyId)

  const { ticketId } = useParams<'ticketId'>()
  const { workOrderId } = useParams<'workOrderId'>()
  const toast = useToast()
  const navigate = useNavigate()

  const { projectData: ticketDetails } = usePCProject(selectedTicketId)

  const {
    isOpen: isDeleteProjecModalOpen,
    onOpen: onDeleteProjecModalOpen,
    onClose: onDeleteProjecModalClose,
  } = useDisclosure()

  const { data: deleteCheck } = usePropertyAllowDelete(Number(propertyId))
  const { mutate: deletePropertyCall, isLoading: deleteLoading } = useDeletePropertyMutation()

  const deleteProject = () => {
    onDeleteProjecModalOpen()
  }

  const confirmDelete = () => {
    deletePropertyCall(Number(propertyId), {
      onSuccess() {
        navigate('/property')
        onDeleteProjecModalClose()
        toast({
          title: 'Property Deleted',
          description: `Property is deleted successfully.`,
          status: 'success',
          duration: 9000,
          isClosable: true,
          position: 'top-left',
        })
      },
      onError(error: any) {
        toast({
          title: 'Property Delete',
          description: (error.title as string) ?? 'Unable to Delete Property.',
          status: 'error',
          duration: 9000,
          isClosable: true,
          position: 'top-left',
        })
      },
    })
  }

  React.useEffect(() => {
    if (ticketId) {
      setSelectedTicketId(Number(ticketId))
    }

    if (workOrderId) {
      setSelectedWoId(Number(workOrderId))
    }
  }, [])

  const { isFPM: isMaintenanceSpecialist } = useUserRolesSelector()

  return (
    <TicketEditContext.Provider
      value={{ selectedTicketId: selectedTicketId, setSelectedTicketId: setSelectedTicketId }}
    >
      <Flex>
        <HStack w="100%" alignItems="stretch" maxHeight="1020px" justifyContent={'center'}>
          <Box flex="1" flexDirection="column" data-testid="work-order-inline-table" w="45%" h="100%">
            <ResidentBox propertyId={propertyId as string} projectId={selectedTicketId} />{' '}
            {/* HN-PSWOA-5380| Prop drilling selected ticket id to update property payload to change the client of the selected ticket as well */}
            <Card
              h="auto"
              mt="10px"
              w="100%"
              py="4px"
              px="14px"
              rounded={6}
              border="1px solid #CBD5E0"
              boxShadow="0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)"
            >
              <Box>
                <HStack m="5px">
                  <Spacer />
                  <Box>
                    {deleteCheck && (
                      <Button
                        fontSize="16px"
                        variant="outline"
                        colorScheme="brand"
                        isDisabled={deleteLoading}
                        onClick={deleteProject}
                      >
                        Delete
                      </Button>
                    )}
                    {!isMaintenanceSpecialist && (
                      <Button
                        colorScheme="brand"
                        fontSize="14px"
                        minW={'80px'}
                        ml="10px"
                        mr="-4px"
                        onClick={() => setSelectedTicketId(null)}
                      >
                        <Icon as={BiBookAdd} fontSize="18px" mr={2} />
                        New
                      </Button>
                    )}
                  </Box>
                </HStack>

                <TicketsInlineTable propertyId={propertyId} />
              </Box>
            </Card>
          </Box>

          <Box
            flex="1"
            m="0 !important"
            flexDirection="column"
            w="55%"
            sx={{
              '&::-webkit-scrollbar': {
                width: '10px',
                height: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#C2C7CD',
                borderRadius: '1px',
                border: '2px solid #f1f1f1',
              },
              scrollbarWidth: 'thin',
            }}
          >
            <Card
              overflowX={selectedTicketId ? 'hidden' : 'auto'}
              h="100%"
              py="10px"
              px="0"
              boxShadow="0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)"
              borderRadius="6px"
              border="1px solid #CBD5E0"
              rounded={6}
              margin="0"
              ml="10px"
              data-testid="cards-container-ticket"
              sx={{
                '&::-webkit-scrollbar': {
                  width: '10px',
                  height: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#C2C7CD',
                  borderRadius: '1px',
                  border: '2px solid #f1f1f1',
                },
                scrollbarWidth: 'thin',
              }}
            >
              {selectedTicketId ? (
                <HStack>
                  <Text fontSize="18px" fontWeight="500" ml="10px" mb="10px" mr="10px">
                    Ticket: {ticketDetails?.woNumber}
                  </Text>
                  <Box pos="relative" top="-5px">
                    <Status value={ticketDetails?.projectStatus as any} id={ticketDetails?.projectStatus as any} />
                  </Box>
                </HStack>
              ) : (
                <Text fontSize="18px" fontWeight="500" ml="10px" mb="10px">
                  New Ticket
                </Text>
              )}

              {selectedTicketId ? (
                <>
                  <Stack ml="10px">
                    <Box mb="10px" mr="10px">
                      <FinancialOverview ticketId={selectedTicketId} />
                    </Box>
                  </Stack>

                  <TicketDetails defaultWorkOrderId={selectedWoId} projectId={selectedTicketId} />
                </>
              ) : (
                <Stack minH="107vh">
                  <NewTicketFlow propertyId={propertyId} />
                </Stack>
              )}
            </Card>
          </Box>
        </HStack>
      </Flex>
      <ConfirmationBox
        title="Delete Property"
        content="Are you sure you want to Delete this Property?"
        isOpen={isDeleteProjecModalOpen}
        onClose={onDeleteProjecModalClose}
        isLoading={deleteLoading}
        onConfirm={confirmDelete}
      />
    </TicketEditContext.Provider>
  )
}

export default TicketEdit
